import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { AuthorizationsService } from '../../../../services/patientmr/authorizations.service';
import { PAUnitType } from '../../../../dtos/patientmr/authorizations.dto';

interface ProgressBarProps {
  used: number;
  total: number | null;
  unitType: PAUnitType | string;
  minutesPerUnit: number | null;
  compact?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ used, total, unitType, minutesPerUnit, compact = false }) => {
  if (!total) return <span className="text-xs text-slate-400">&mdash;</span>;

  const pct = Math.min(Math.round((used / total) * 100), 100);
  const isWarning = pct >= 80;
  const barColor = AuthorizationsService.getProgressBarColor(used, total);
  const hasHours = unitType === PAUnitType.UNITS && minutesPerUnit;

  if (compact) {
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
          {AuthorizationsService.formatQuantity(used, unitType, minutesPerUnit, true)}/
          {AuthorizationsService.formatQuantity(total, unitType, minutesPerUnit, true)}
        </span>
      </div>
    );
  }

  const remaining = total - used;
  const uLabel = AuthorizationsService.getUnitLabel(unitType);

  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700">
          {used} of {total} {uLabel} used
          {hasHours && (
            <span className="text-slate-400 font-normal ml-1">
              ({AuthorizationsService.toHours(used, unitType, minutesPerUnit)} of {AuthorizationsService.toHours(total, unitType, minutesPerUnit)} hrs)
            </span>
          )}
        </span>
        <span className={`text-sm font-semibold tabular-nums ${pct >= 100 ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-600'}`}>
          {pct}%
        </span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-slate-400">
          {remaining} {uLabel} remaining
          {hasHours ? ` (${AuthorizationsService.toHours(remaining, unitType, minutesPerUnit)} hrs)` : ''}
        </span>
        {isWarning && pct < 100 && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle size={12} /> Approaching limit
          </span>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
